import { CommonModule } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTypographyModule } from 'ng-zorro-antd/typography';

import { environment } from '@env/environment';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { AlertsCenterCounterComponent } from '@app/alerts/components/counter/alerts-center-counter.component';
import { AlertsCenterDrawerComponent } from '@app/alerts/components/drawer/alerts-center-drawer.component';
import { AccountLimitDialogComponent } from '@app/core/components/account-limit-dialog/account-limit-dialog.component';
import { TourModule } from '@app/shared/modules/tour/tour.module';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { DaterangepickerComponent, ExportTableDataComponent, GlobalRefinersComponent } from './components';
import { CurrencyInputComponent } from './components/currency-input/currency-input.component';
import { CustomSorterComponent } from './components/custom-sorter/custom-sorter.component';
import { CustomerSelectorComponent } from './components/customer-selector/customer-selector.component';
import { DismissBannerComponent } from './components/dismiss-banner/dismiss-banner.component';
import { GrowthMetricComponent } from './components/growth-metric/growth-metric.component';
import { LoginFormComponent } from './components/login-form/login-form.component';
import { MetricsMenuComponent } from './components/metric-menu/metric-menu.component';
import { ReLoginModalComponent } from './components/re-login-modal/re-login-modal.component';
import { ReadMoreComponent } from './components/read-more/read-more.component';
import { SnoozeModeAccountComponent } from './components/snooze-mode-account/snooze-mode-account.component';
import { SortMenuComponent } from './components/sort-menu/sort-menu.component';
import { TableFilterMenuMetricComponent } from './components/table-filter-menu-metric/table-filter-menu-metric.component';
import { TableFilterMenuComponent } from './components/table-filter-menu/table-filter-menu.component';
import { TableModule } from './components/table/table.module';
import { TagComponent } from './components/tag/tag.component';
import { AdvancedFilterModule } from './modules/advanced-filter/advanced-filter.module';
import { DateRangeModule } from './modules/date-range-selector/date-range.module';
import { GlobalFiltersModule } from './modules/global-filters/global-filters.module';
import { SharedDirectivesModule } from './shared-directives.module';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, environment.app.translationsUrl, '.json');
}

const designLibraryModules = [
  NzAlertModule,
  NzAlertModule,
  NzAutocompleteModule,
  NzButtonModule,
  NzBadgeModule,
  NzCheckboxModule,
  NzCollapseModule,
  NzDatePickerModule,
  NzDropDownModule,
  NzDividerModule,
  NzDrawerModule,
  NzFormModule,
  NzInputModule,
  NzIconModule,
  NzListModule,
  NzLayoutModule,
  NzModalModule,
  NzMenuModule,
  NzMessageModule,
  NzNotificationModule,
  NzProgressModule,
  NzPopoverModule,
  NzPopconfirmModule,
  NzRadioModule,
  NzSpinModule,
  NzSelectModule,
  NzSwitchModule,
  NzTypographyModule,
  NzTableModule,
  NzTagModule,
  NzTabsModule,
  NzToolTipModule,
];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ScrollingModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    [...designLibraryModules],
    SharedDirectivesModule,
    AdvancedFilterModule,
    DateRangeModule,
    GlobalFiltersModule,
    TourModule,
    AlertsCenterCounterComponent,
    AlertsCenterDrawerComponent,
    CustomerSelectorComponent,
    CurrencyInputComponent,
    MetricsMenuComponent,
    TagComponent,
    ReadMoreComponent,
    LoginFormComponent,
  ],
  declarations: [
    GrowthMetricComponent,
    GlobalRefinersComponent,
    ReLoginModalComponent,
    ExportTableDataComponent,
    DaterangepickerComponent,
    SnoozeModeAccountComponent,
    CustomSorterComponent,
    DismissBannerComponent,
    TableFilterMenuComponent,
    TableFilterMenuMetricComponent,
    AccountLimitDialogComponent,
    SortMenuComponent,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ScrollingModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    [...designLibraryModules],
    MetricsMenuComponent,
    GrowthMetricComponent,
    GlobalRefinersComponent,
    ReLoginModalComponent,
    ExportTableDataComponent,
    DaterangepickerComponent,
    SnoozeModeAccountComponent,
    ReadMoreComponent,
    CustomSorterComponent,
    SharedDirectivesModule,
    TableModule,
    DismissBannerComponent,
    TableFilterMenuComponent,
    TableFilterMenuMetricComponent,
    SortMenuComponent,
    TagComponent,
    CurrencyInputComponent,
  ],
  providers: [provideHttpClient(withInterceptorsFromDi())],
})
export class SharedModule {}
